import * as STD from '@emersya/mpa-std';
import * as CM from '@emersya/mpa-components-manager';

export interface IShortcutDescriptor {
	type?: 'down' | 'up' | 'down-up'; // default = down;
	code: string;
	method: CM.PropertyOperation.CallOperation.IDescriptor;
	/**
	 * A shortcut is applied on the current hovered component, selectors are used to select only desired components.
	 * See "Component.component_matches" method for the syntax.
	 **/
	selectors: string[];
};

export interface IShortcut extends Omit<IShortcutDescriptor, 'type' | 'method' | 'code'> {
	type: NonNullable<IShortcutDescriptor['type']>;
	sequence: string[];
	method: CM.PropertyOperation.CallOperation;
	selectors: string[];
};

export interface IShortcutsManagerComponentConfiguration extends CM.Component.IConfiguration {
	shortcuts?: IShortcutDescriptor[];
	useDebug?: boolean;
};

export class ShortcutsManagerComponent extends CM.Component<
	ShortcutsManagerComponent,
	IShortcutsManagerComponentConfiguration
> {
	static count = 0;
	shortcuts: IShortcut[] = [];
	currentlyPressedKeys = new Set<string>;
	private previouslyDisplayedDebuggedKeyPress: Element | null = null;

	async onShortcutPressed(shortcut: IShortcut) {
		if (!shortcut.selectors.includes('*')) {
			const currentlyHoveredComponent = (<ApplicationNS.Component>CM.Manager.root).currentlyHovered?.component;
			if (!currentlyHoveredComponent) return;

			const isSelected = shortcut.selectors.some(x => currentlyHoveredComponent.component_matches(x));
			if (!isSelected) return;
		}

		await shortcut.method.execute(this, this.component_getEventWhichRepresentNoEvent());
	};

	async onKeyDown(key: string, code: string) {
		this.currentlyPressedKeys.add(key + '|' + code);

		const currentlyPressedKeys = Array
			.from(this.currentlyPressedKeys.keys())
			.map(keyCode => <[string, string]>keyCode.split('|'));

		const shortcuts = this.shortcuts.filter(shortcut => (
			shortcut.type !== 'up' &&
			ShortcutsManagerComponent.matchShortcut(shortcut.sequence, currentlyPressedKeys)
		));

		for (const shortcut of shortcuts) {
			await this.onShortcutPressed(shortcut);
		}
	};

	onKeyUp(key: string, code: string) {
		this.currentlyPressedKeys.delete(key + '|' + code);
	}

	private async onNativeKeyDown(event: KeyboardEvent) {
		await this.onKeyDown(event.key, event.code);
	};

	private onNativeKeyUp(event: KeyboardEvent) {
		this.onKeyUp(event.key, event.code);
	};

	private onBlur() {
		this.currentlyPressedKeys.clear();
	};

	override async component_onActivationChange(event: STD.events.Event<any, CM.Component<any, any>>) {
		await super.component_onActivationChange(event);
		if (event.subType === 'enabled') {
			document.addEventListener('keydown', this.onNativeKeyDown.bind(this));
			document.addEventListener('keyup', this.onNativeKeyUp.bind(this));
			document.addEventListener('blur', this.onBlur.bind(this));

		} else {
			document.removeEventListener('keydown', this.onNativeKeyDown.bind(this));
			document.removeEventListener('keyup', this.onNativeKeyUp.bind(this));
			document.removeEventListener('blur', this.onBlur.bind(this));
		}
	};

	addShortcut(descriptor: IShortcutDescriptor) {
		const MCVG = new CM.PropertyOperation.CallOperation(descriptor.method);
		const shortcut = Object.assign(
			{
				type: 'down'
			},
			descriptor,
			{
				method: MCVG,
				sequence: descriptor.code.split('+')
			}
		);
		this.shortcuts.push(shortcut);
	};

	matchShortcut(sequence: string | string[]) {
		if (typeof sequence === 'string')
			sequence = sequence.split('+');

		const currentlyPressedKeys = Array
			.from(this.currentlyPressedKeys.keys())
			.map(keyCode => <[string, string]>keyCode.split('|'));

		return ShortcutsManagerComponent.matchShortcut(sequence, currentlyPressedKeys);
	}

	private static matchShortcut(sequence: string[], currentlyPressedKeys: [string, string][], idx: number = 0): boolean {
		if (idx === currentlyPressedKeys.length && idx === sequence.length)
			return true;

		if (idx >= currentlyPressedKeys.length || idx >= sequence.length)
			return false;

		return (
			currentlyPressedKeys[idx]!.includes(sequence[idx]!) &&
			this.matchShortcut(sequence, currentlyPressedKeys, idx + 1)
		);
	}

	constructor(
		descriptor: ConstructorParameters<typeof CM.Component>[0],
		configuration: IShortcutsManagerComponentConfiguration
	) {
		super(descriptor, configuration);
		if (ShortcutsManagerComponent.count++ !== 0) {
			this.component_throwUserError('Already one shortcuts-manager instance!', {
				ShortcutsManagerComponent
			});
		}
		if (configuration.useDebug) {
			document.addEventListener('keydown', (ev) => {
				const DOM = STD.DOM.factory({
					style: {
						position: 'absolute',
						zIndex: '550000',
						bottom: '20px',
						right: '20px',
						border: '1px solid black',
						backgroundColor: 'white',
						borderRadius: '6px',
						display: 'flex',
						flexDirection: 'row',
						overflow: 'hidden'
					},
					childs: [
						{
							text: ev.key,
							style: {
								backgroundColor: '#bcbcbc',
								padding: '6px 8px'
							}
						}, {
							text: ev.code,
							style: {
								padding: '6px 8px'
							}
						}
					]
				});
				document.body.appendChild(DOM);
				if (this.previouslyDisplayedDebuggedKeyPress) {
					this.previouslyDisplayedDebuggedKeyPress.remove();
				}
				this.previouslyDisplayedDebuggedKeyPress = DOM;
				setTimeout(() => {
					if (!DOM.isConnected) {
						return;
					}
					this.previouslyDisplayedDebuggedKeyPress = null;
					DOM.remove();
				}, 2500);
			});
		}
		for (const shortcutDescriptor of configuration.shortcuts || []) {
			this.addShortcut(shortcutDescriptor);
		}
	};
};

export namespace NS {
	export const Component = ShortcutsManagerComponent;
	export type Component = ShortcutsManagerComponent;
	export type Configuration = IShortcutsManagerComponentConfiguration;
}
import {NS as ApplicationNS} from '../Application/Application';

CM.Manager.declare({
	id: 'shortcuts-manager',
	type: ShortcutsManagerComponent
});
